import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, apiLogin } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { useTheme } from '../lib/theme.js';
import { Logo } from '../components/Logo.js';
import { Button, Card, CardBody, Input } from '../components/ui/index.js';

/**
 * Sign-in. The API reports which mode it is running and this screen renders
 * the matching control:
 *
 *  - `oidc` (production): a single button that hands off to the identity
 *    provider. The whole authorization-code + PKCE exchange happens
 *    server-side, so this page never handles a code or a token.
 *  - `synthetic` (development): the seeded-identity form, which drives the
 *    same token-validation pipeline including the MFA-claim check.
 */
export function LoginPage() {
  const [email, setEmail] = useState('');
  const [mfa, setMfa] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'synthetic' | 'oidc' | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const { theme } = useTheme();

  useEffect(() => {
    // A failed callback returns here with a fixed code — never provider text.
    const reason = new URLSearchParams(window.location.search).get('error');
    if (reason === 'mfa') {
      setError('Sign-in requires MFA or passkey assurance. Complete the challenge and retry.');
    } else if (reason === 'oidc') {
      setError('Sign-in did not complete. Try again, or contact your platform owner.');
    }

    void fetch('/api/v1/auth/config', { credentials: 'same-origin' })
      .then((res) => (res.ok ? res.json() : null))
      .then((body: { mode?: 'synthetic' | 'oidc' } | null) => setMode(body?.mode ?? 'synthetic'))
      // Falling back to the synthetic form keeps local development working
      // even if the API is not up yet; production simply shows nothing usable.
      .catch(() => setMode('synthetic'));
  }, []);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await apiLogin(email, mfa);
      await refresh();
      navigate('/');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'MFA_REQUIRED') {
        setError('Sign-in requires MFA or passkey assurance. Enable the MFA option to continue.');
      } else if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        setError('This identity is not a provisioned Control operator.');
      } else {
        setError('Sign-in failed. Check the API is reachable and try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="flex flex-col items-center gap-2">
          <Logo
            variant="full"
            tone={theme === 'dark' ? 'on-dark' : 'on-light'}
            height={40}
            title="HospaSense Control"
          />
          <p className="text-xs uppercase tracking-widest text-text-muted">
            Platform-owner control plane
          </p>
        </div>
        <Card>
          <CardBody className="pt-6">
            {mode === 'oidc' ? (
              <div className="space-y-4">
                {error && (
                  <p
                    role="alert"
                    className="rounded-lg bg-error-bg-subtle px-3 py-2 text-xs text-error"
                  >
                    {error}
                  </p>
                )}
                <Button
                  fullWidth
                  onClick={() => {
                    window.location.assign('/api/v1/auth/oidc/start');
                  }}
                >
                  Continue with SenseSys ID
                </Button>
                <p className="text-center text-xs text-text-muted">
                  You will be asked for your authenticator code.
                </p>
              </div>
            ) : (
              <form onSubmit={(e) => void submit(e)} className="space-y-4" aria-label="Sign in">
                <Input
                  label="Work email"
                  type="email"
                  required
                  autoComplete="username"
                  placeholder="you@sensesys.example"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  hint="Synthetic development identities only (see seeded users)."
                />
                <label className="flex items-center gap-2 text-sm text-text-secondary">
                  <input
                    type="checkbox"
                    checked={mfa}
                    onChange={(e) => setMfa(e.target.checked)}
                    className="size-4 accent-(--primary)"
                  />
                  Present MFA / passkey assurance
                </label>
                {error && (
                  <p
                    role="alert"
                    className="rounded-lg bg-error-bg-subtle px-3 py-2 text-xs text-error"
                  >
                    {error}
                  </p>
                )}
                <Button type="submit" fullWidth loading={busy}>
                  Sign in
                </Button>
              </form>
            )}
          </CardBody>
        </Card>
        <p className="text-center text-xs text-text-muted">
          Access is deny-by-default and every privileged action is audited.
        </p>
      </div>
    </div>
  );
}
